
import React, { useEffect, useMemo, useState } from 'react'
import { Col, FormGroup, FormLabel, Row } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import Swal from 'sweetalert2'

import { useProgrammation } from '@/Hooks/UseProgrammation'
import useTranslation from '@/Hooks/UseTranslation'

import { deleteRelation, updateRelation } from '@/Services/ProgrammationManagerService'
import { removeRelationAction, updateRelationsAction } from '@/Redux/Actions/ProgrammationManagerActions'

import Modal from '@/Components/Modal/Modal'
import InputSelect from '../../components/InputSelect'

import Spinner from '@/Components/SvgImages/Spinner/Spinner'
import TrashIcon from '@/Components/SvgImages/TrashIcon'

import './RelationEditor.scss'
import { getItemsOptionsFromTaskList, getWorksOptionsFromTaskList } from './RelationsHelper'
import InputText from '../../components/InputText'

interface Props {
  relation: any
  onClose: () => void
}

const RelationEditor = ({ relation, onClose }: Props) => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { tasks } = useSelector((state: any) => state.ProgrammationReducer)
  const macroResult = useSelector((state: any) => state.MacroReducer.result)

  const { itemOpened, patchItemOpened } = useProgrammation()

  const [ fromTask, setFromTask ] = useState<any>()
  const [ toTask, setToTask ] = useState<any>()
  const [ type, setType ] = useState<any>()
  const [ saving, setSaving ] = useState(false)
  const [ deleting, setDeleting ] = useState(false)

  const relationsTypes = [
    {
      value: 'start_to_start',
      label: t('RELATIONS_CREATOR:Starts'),
    },
    {
      value: 'end_to_start',
      label: t('RELATIONS_CREATOR:Ends'),
    },
  ]

  const tasksOptions = useMemo(() => {

    if (itemOpened.type !== 'trabajo') {

      return getItemsOptionsFromTaskList({
        list: macroResult,
        currentItem: itemOpened,
        currentLabel: t('RELATIONS_CREATOR:CurrentItem'),
        noNameText: t('RELATIONS_CREATOR:Nameless'),
        level: 0,
      })
    } else if (itemOpened.type === 'trabajo') {

      return getWorksOptionsFromTaskList({
        list: macroResult,
        currentWork: itemOpened,
        currentLabel: t('RELATIONS_CREATOR:CurrentWork'),
        noNameText: t('RELATIONS_CREATOR:Nameless'),
        level: 0,
      })
    }
  }, [macroResult, itemOpened])

  const validateRelation = (relation: any) => {

    const isValid = true

    const taskFromIsCurrentItem = relation.from_task === itemOpened._id
    const taskToIsCurrentItem = relation.to_task === itemOpened._id

    if (!taskFromIsCurrentItem && !taskToIsCurrentItem) {

      Swal.fire({
        title: t('RELATION_ITEM:TaskNotSelectedTitle'),
        text: t('RELATION_ITEM:TaskNotSelectedContent'),
        icon: 'warning',
        confirmButtonText: t('RELATION_ITEM:OkButton'),
        width: '500px',
      })
      return false
    }

    return isValid
  }

  const handleSaveRelation = async (relation: any) => {

    if (!validateRelation(relation)) return

    setSaving(true)

    console.log('relation', relation)

    const [ error, data ] = await updateRelation(relation._id, relation, itemOpened._id)

    setSaving(false)

    if (error) {
      return console.log(error)
    }

    patchItemOpened({
      relations: itemOpened.relations.map((r: any) => r._id === relation._id ? relation : r)
    })

    dispatch(updateRelationsAction(data))
  }

  const handleChangeTaskFrom = (task_id: string) => {

    setFromTask(tasks.find((task: any) => task._id === task_id))

    handleSaveRelation({
      _id: relation._id,
      type,
      from_task: task_id,
      to_task: toTask._id,
    })
  }

  const handleChangeType = (type: string) => {

    setType(type)

    handleSaveRelation({
      _id: relation._id,
      type,
      from_task: fromTask._id,
      to_task: toTask._id,
    })
  }

  const handleChangeTaskTo = (task_id: string) => {

    setToTask(tasks.find((task: any) => task._id === task_id))

    handleSaveRelation({
      _id: relation._id,
      type,
      from_task: fromTask._id,
      to_task: task_id,
    })
  }

  const handleDelete = async () => {

    setDeleting(true)

    const [ error, data ] = await deleteRelation(relation._id)

    setDeleting(false)

    if (error) {
      return console.log(error)
    }

    onClose()

    patchItemOpened({
      relations: itemOpened.relations.filter((r: any) => r._id !== relation._id)
    })

    dispatch(removeRelationAction({ relation_id: relation._id, ...data }))
  }

  useEffect(() => {

    const getTasks = () => {

      const fromTask = tasks.find((task: any) => task._id === relation.from_task)
      const toTask = tasks.find((task: any) => task._id === relation.to_task)

      setFromTask(fromTask)
      setToTask(toTask)
    }

    if (relation) {

      setType(relation.type)
      getTasks()
    }
  }, [ relation ])

  if (!fromTask || !toTask) return null

  return (
    <Modal
      show={!!relation}
      title={t('RELATION_EDITOR:Title')}
      onClose={onClose}
      optionsButtons={[{
        icon: (deleting
                ? <Spinner height={20} width={20} fill="#e21c1c" />
                : <TrashIcon height={20} width={20} color="#e21c1c" />
        ),
        onClick: () => handleDelete(),
        disabled: deleting
      }]}
      backdropClassName='relation-editor-backdrop'
      className='relation-editor'
      showCloseButton={true}
    >
      {relation?.relation && (
        <>
          {t('RELATION_ITEM:RelationType')}
        </>
      )}
      {relation && !relation?.relation && (
        <>
          <FormGroup>
            <FormLabel>
              {t('RELATIONS_CREATOR:When')}
            </FormLabel>
            <Row>
              <Col xs={8} className='col-task-right'>
                <InputSelect
                  value={fromTask._id}
                  options={tasksOptions}
                  onChange={handleChangeTaskFrom}
                  renderOption={(option: any) => (
                    <>
                      {option.isGroup && (
                        <optgroup key={option.value} label={option.label}></optgroup>
                      )}
                      {!option.isGroup && (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      )}
                    </>
                  )}
                  disabled={saving}
                />
              </Col>
              <Col xs={4} className='col-task-left'>
                <InputSelect
                  value={type || 'start_to_start'}
                  options={relationsTypes}
                  onChange={handleChangeType}
                  renderOption={(option: any) => (
                    <>
                      {option.isGroup && (
                        <optgroup key={option.value} label={option.label}></optgroup>
                      )}
                      {!option.isGroup && (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      )}
                    </>
                  )}
                  disabled={saving}
                />
              </Col>
            </Row>
          </FormGroup>
          <FormGroup>
            <FormLabel>
              {t('RELATIONS_CREATOR:Then')}
            </FormLabel>
            <Row>
              <Col xs={8} className='col-task-right'>
                <InputSelect
                  value={toTask._id}
                  options={tasksOptions}
                  onChange={handleChangeTaskTo}
                  renderOption={(option: any) => (
                    <>
                      {option.isGroup && (
                        <optgroup key={option.value} label={option.label}></optgroup>
                      )}
                      {!option.isGroup && (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      )}
                    </>
                  )}
                  disabled={saving}
                />
              </Col>
              <Col xs={4} className='col-task-left'>
                  <InputText
                    value={t('RELATIONS_CREATOR:Starts')}
                    disabled
                  />
              </Col>
            </Row>
          </FormGroup>
        </>
      )}
    </Modal>
  )
}

export default RelationEditor
